if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        // Cats n Scribbles: her own tap takes the place of a retry already waiting (addToBag), on this form or on another
        // size of the same card (its size pills are a form each). A tap made while the first add or its retry is still
        // on its way cancels nothing, so both sizes go in, as they would with no 429.
        clearTimeout(this.retryTimer);
        const pills = this.closest('.cns-card__pills');
        if (pills) pills.querySelectorAll('product-form').forEach((f) => f !== this && clearTimeout(f.retryTimer));

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        this.addToBag(evt, config, false);
      }

      // Cats n Scribbles: Dawn's add, moved out of onSubmitHandler so a turned away add can go once more with the
      // same form data (retry = this is that second try)
      addToBag(evt, config, retry) {
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const variantId = config.body.get('id');
        const quantity = parseInt(config.body.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        let answered = false;

        fetch(`${routes.cart_add_url}`, config)
          // Cats n Scribbles: a rate limit (429, Shopify's json or a "verifying your connection" page) or any answer
          // that is not json (an error or challenge page) added nothing; parsing it threw and the button did nothing.
          // page: the answer was a page, not json
          .then((response) =>
            response.text().then((text) => {
              let json = null;
              try {
                json = JSON.parse(text);
              } catch (e) {
                json = null;
              }
              if (response.status === 429 || !json) {
                throw Object.assign(new Error(`add: ${response.status}`), { status: response.status, page: !json });
              }
              return json;
            })
          )
          .then((response) => {
            answered = true;
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              // Cats n Scribbles: in the bag's words when her bag already holds every one left (inBag), else in
              // Shopify's. The product page says it under the button. A card's quick add (its size pills, its one-size
              // "add") has no message slot, so the toast says it there, for the in-bag refusal only: Shopify's other
              // sentences ("The product '...' is already sold out.") stay off the toast, as on live, where the pill
              // reads sold out (Oct 31 QA). The gift card page keeps Dawn's hidden errors, as its recipient form says them
              const inBag = this.inOurWords(response.description);
              const slot = this.querySelector('.product-form__error-message-wrapper');
              const words = inBag || (slot ? response.description : null);
              if (words && !this.hideErrors) this.say(words, !retry);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              // Cats n Scribbles: a piece already all in her bag is not sold out: its size or "add" stays as it is (a
              // card's pill read "sold out" for the rest of the visit) (Oct 31 review)
              if (inBag) return;
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
            // Cats n Scribbles: the bag answered and this came after it (painting the drawer): the piece is in
            if (answered) return;
            // Cats n Scribbles: Shopify's "verifying your connection" check (a 429 or 403 page, not json) clears only on a
            // page load, and every fetch gets the same page until then: post the form itself, so the browser shows the
            // check and lands on /cart with the piece. Shopify's json 429 keeps the retry below.
            if (e.page && (e.status === 429 || e.status === 403)) {
              this.form.submit();
              return;
            }
            // Cats n Scribbles: say it under the button (role alert; a card's quick add has no slot, so the toast), in the
            // bag's words. A 429 turned the add away, so it goes once more by itself after a moment (her own tap first
            // cancels that). An error page or a dropped connection may have added it: never sent again, and the bag reads
            // itself so the header count shows. The last two words are joined, so none sits alone on a line. Only the
            // answer to her own tap brings the sentence into view (reveal); the retry's answer leaves the page where it is.
            const busy = e.status === 429;
            const again = busy && !retry;
            this.say(
              again
                ? "that didn't go through. trying again in a\u00a0moment."
                : busy
                ? "that didn't go through. try again in a\u00a0moment."
                : e.status
                ? 'that may not have gone through. check your bag, then try\u00a0again.'
                : 'the connection dropped. check your bag, then try\u00a0again.',
              !retry
            );
            if (again) {
              // Cats n Scribbles: only for the size she is still on, and on time. A page she leaves keeps its timers in
              // the back/forward cache and runs them when she comes back: then it waits for her tap instead.
              const due = Date.now() + 2500;
              this.retryTimer = setTimeout(() => {
                if (!this.isConnected || String(this.variantIdInput.value) !== String(config.body.get('id'))) return;
                if (document.visibilityState === 'visible' && Date.now() - due < 1000) this.addToBag(evt, config, true);
                else this.say("that didn't go through. try\u00a0again.");
              }, 2500);
            } else if (!busy && window.CNSCart && typeof window.CNSCart.refresh === 'function') {
              window.CNSCart.refresh();
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            // Cats n Scribbles: only an add the bag answered can have filled an empty bag
            if (answered && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // Cats n Scribbles: an add from the phone's sticky bar (it clicks this form's button, scrolled off the top) wrote
      // its failure off screen: bring it into view. Only the add's own failures call this; the size gate scrolls
      // (smoothly) by itself.
      reveal() {
        const w = this.errorMessageWrapper;
        if (!w || w.hidden) return;
        const r = w.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) w.scrollIntoView({ block: 'center' });
      }

      // Cats n Scribbles: a failed add's sentence (show = it answers her tap: bring it into view). A card's quick add
      // (its size pills, its one-size "add") has no message slot, and the gift card page hides its own (data-hide-errors),
      // so the site's toast says it there, for 6 seconds: a sentence this long needs longer than the toast's 2.4.
      say(msg, show) {
        if (!this.hideErrors && this.querySelector('.product-form__error-message-wrapper')) {
          this.handleErrorMessage(msg);
          if (show) this.reveal();
          return;
        }
        if (!window.CNS || typeof window.CNS.toast !== 'function') return;
        // the toast's own timer, so its words leave the live region once it fades (Oct 31 QA: this sentence stayed)
        window.CNS.toast(msg, 6000);
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      // Cats n Scribbles: an add turned away because the bag already holds every piece left, in the bag's words
      // (cns-cart.js voice), not Shopify's 'The maximum quantity of this item is already in your cart.' (Oct 31 QA);
      // null for any other refusal, which keeps Shopify's own sentence. 'All N ... are in your cart.' gives the count;
      // 'maximum quantity' doesn't, and a size with 2 or more left can be all in her bag, so it says no count (it read
      // 'the last one') (Oct 31 review)
      inOurWords(said) {
        const t = String(said || '').trim();
        const all = t.match(/^all (\d+) .*in your cart/i);
        if (all && all[1] === '1') return 'the last one is already in your\u00a0bag.';
        if (all) return `all ${all[1]} left are already in your\u00a0bag.`;
        if (/maximum quantity.*already in your cart/i.test(t)) return "all that's left is already in your\u00a0bag.";
        return null;
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        // Cats n Scribbles: an add Shopify turns away, a dropped connection or a failed bag read rejects this promise;
        // marked handled here, as in cart.js, so it no longer throws an uncaught error (the form or the toast says it in
        // its own words) (Oct 31 review). The event carries its own copy of the promise (Shopify's standard-events
        // wraps it in a .then), so that one is marked handled too (Oct 31 QA). The button is already busy here: a
        // standard-events that changes under us (no createPromise, a constructor that throws) means an add made
        // without its event, never a button left loading (Oct 31 review)
        try {
          const deferred = CartLinesUpdateEvent.createPromise();
          deferred.promise.catch(() => {});
          const event = new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          });
          event.promise?.catch?.(() => {});
          this.dispatchEvent(event);
          return deferred;
        } catch (e) {
          return null;
        }
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
